<template>
  <div
    class="queue-card w-full rounded border border-dashed border-slate-300 bg-white px-2 py-1 text-left"
    :class="{ 'cursor-grab hover:bg-blue-50 hover:shadow-[0_0_0_2px_#bfdbfe]': canWrite, 'queue-card--linked': linked }"
    :style="{ borderLeft: `3px solid ${color}` }"
    :data-block-key="partial ? po.poNumber : undefined"
    :role="canWrite ? 'button' : undefined"
    :tabindex="canWrite ? 0 : undefined"
    :draggable="canWrite"
    @click="canWrite && emit('schedule')"
    @keydown.enter.prevent="canWrite && emit('schedule')"
    @keydown.space.prevent="canWrite && emit('schedule')"
    @mouseenter="onHoverStart"
    @mouseleave="onHoverEnd"
    @dragstart="onDragStart"
    @dragend="emit('dragEnd')"
  >
    <span class="flex items-center gap-1 text-[11px] font-semibold text-gray-900">
      <span class="grow truncate">{{ po.poNumber }}</span>
      <span class="shrink-0 text-[10px] font-medium text-gray-500">{{ orderedOn }}</span>
    </span>
    <span class="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-gray-500">
      <span class="min-w-0 flex-1 truncate">{{ remaining }}</span>
      <span v-if="partial" class="rounded-full border border-blue-200 bg-blue-50 px-2 text-[10px] font-semibold text-blue-700">Partially scheduled</span>
    </span>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import { useAssignmentHover } from '@/composables/useAssignmentHover';
  import type { PurchaseOrder } from '@/types/purchaseOrders';
  import { isPartiallyScheduled, remainingLine } from '@/utils/toScheduleQueue';

  const props = defineProps<{ po: PurchaseOrder; color: string; canWrite: boolean }>();
  const emit = defineEmits<{ schedule: []; dragStart: []; dragEnd: [] }>();

  const { hoveredBlockKey, setHoveredBlock, clearHoveredBlock } = useAssignmentHover();
  const thisCardSetHover = ref(false);

  const remaining = computed(() => remainingLine(props.po));
  const partial = computed(() => isPartiallyScheduled(props.po));
  // Rings the card whenever its PO is singled out anywhere on the board — from here or from one
  // of its scheduled blocks — the same way the blocks ring each other.
  const linked = computed(() => partial.value && hoveredBlockKey.value === props.po.poNumber);
  // Odoo datetime text; only the date matters here and it sorts the same way it reads.
  const orderedOn = computed(() => (props.po.orderDate ? props.po.orderDate.slice(0, 'YYYY-MM-DD'.length) : '—'));

  // A partially scheduled PO shares the block key of its scheduled blocks, so hovering the card
  // lights them up in every week they touch and the grid draws its connector from here to
  // them. Nothing is scheduled for the other cards, so there is nothing to point at.
  function onHoverStart(): void {
    if (!partial.value) return;
    setHoveredBlock(props.po.poNumber);
    thisCardSetHover.value = true;
  }

  function onHoverEnd(): void {
    if (!thisCardSetHover.value) return;
    clearHoveredBlock();
    thisCardSetHover.value = false;
  }

  function onDragStart(event: DragEvent): void {
    if (!props.canWrite) return;
    onHoverEnd();
    event.dataTransfer?.setData('text/plain', props.po.poNumber);
    emit('dragStart');
  }
</script>

<style scoped>
  /* Matches .schedule-card--hovered on the week cells, so the queue card and its blocks read
     as one thing when either end is pointed at. */
  .queue-card--linked {
    box-shadow: 0 0 0 2px rgb(191 219 254);
  }
</style>
